import { FITNESS_WORKOUT_FINISH_LIMITS as LIMITS } from '../../../supabase/functions/_shared/fitnessWorkoutFinishDomain';
import { getExplicitFitnessMovement } from '@/lib/fitnessExerciseIdentity';
import { fitnessExercisePresentationName } from '@/lib/fitnessExercisePresentation';
import { getSessionKind, type WorkoutSessionRow } from '@/lib/fitness/sessionKind';
import { readPerformedWorkoutExercises } from '@/lib/fitness/workoutHistory';

export type ExerciseProgressRange = 30 | 90 | 'all';
export interface ExerciseProgressIdentity { key: string; name: string; muscle: string; timed: boolean }
export interface ExerciseProgressPoint {
  sessionId: string; sessionName: string; completedAt: string; at: number;
  sets: number; maxLoadKg: number | null; maxReps: number | null;
  maxSeconds: number | null; volumeKg: number | null; repsLoadKg: number | null;
}
export interface ExerciseProgressEntry extends ExerciseProgressIdentity { points: ExerciseProgressPoint[] }
export interface ExerciseProgressRecord { value: number; completedAt: string; sessionId: string; loadKg?: number }
export interface ExerciseProgressRecords {
  maxLoadKg: ExerciseProgressRecord | null; maxReps: ExerciseProgressRecord | null;
  maxSeconds: ExerciseProgressRecord | null; volumeKg: ExerciseProgressRecord | null;
}

const normalize = (value: string) => value.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();

/** Only explicit app identities are aliases. Never infer equipment, angle, or a translated name. */
export function exerciseProgressIdentity(name: string, muscle = '', timed = false): ExerciseProgressIdentity | null {
  if (!name.trim() || name.length > LIMITS.name) return null;
  const presentation = fitnessExercisePresentationName(normalize(name) === 'dumbbell press' ? 'Dumbbell Press' : normalize(name), normalize(muscle));
  const movement = getExplicitFitnessMovement(presentation);
  const canonicalName = movement?.canonicalName ?? name.trim();
  const isTimed = timed || movement?.unit === 'seconds';
  return {
    key: (movement ? 'movement:' + movement.id : 'source:' + JSON.stringify([normalize(name), normalize(muscle)])) + (isTimed ? ':seconds' : ':reps'),
    name: canonicalName, muscle: muscle.trim(), timed: isTimed,
  };
}

/** Full, account-owned completed gym history. Zero external load never estimates body weight. */
export function buildExerciseProgress(rows: readonly WorkoutSessionRow[], ownerId: string, now = Date.now()): ExerciseProgressEntry[] {
  if (!ownerId) return [];
  const entries = new Map<string, ExerciseProgressEntry>();
  const seen = new Set<string>();
  const ordered = rows.filter(row => row.user_id === ownerId && row.completed_at
    && Number.isFinite(Date.parse(row.completed_at)) && Date.parse(row.completed_at) <= now && getSessionKind(row) === 'gym')
    .slice().sort((a, b) => Date.parse(a.completed_at!) - Date.parse(b.completed_at!) || a.id.localeCompare(b.id));
  for (const row of ordered) {
    if (!row.id || seen.has(row.id)) continue;
    seen.add(row.id);
    const session = new Map<string, { identity: ExerciseProgressIdentity; sets: Array<{ weight: number; reps: number }> }>();
    for (const exercise of readPerformedWorkoutExercises(row.exercises)) {
      const identity = exerciseProgressIdentity(exercise.name, exercise.muscle, exercise.timed);
      const sets = exercise.sets.filter(set => set.type !== 'warmup');
      if (!identity || !sets.length) continue;
      const current = session.get(identity.key);
      if (current) current.sets.push(...sets);
      else session.set(identity.key, { identity, sets: [...sets] });
    }
    for (const { identity, sets } of session.values()) {
      const maxLoad = Math.max(...sets.map(set => set.weight));
      const bestReps = sets.reduce((best, set) => set.reps > best.reps || (set.reps === best.reps && set.weight > best.weight) ? set : best);
      const volume = sets.reduce((sum, set) => sum + set.weight * set.reps, 0);
      const point: ExerciseProgressPoint = {
        sessionId: row.id, sessionName: row.name ?? '', completedAt: row.completed_at!, at: Date.parse(row.completed_at!),
        sets: sets.length, maxLoadKg: !identity.timed && maxLoad > 0 ? maxLoad : null,
        maxReps: !identity.timed ? bestReps.reps : null, maxSeconds: identity.timed ? bestReps.reps : null,
        volumeKg: !identity.timed && volume > 0 ? Math.round(volume * 100) / 100 : null,
        repsLoadKg: !identity.timed ? bestReps.weight : null,
      };
      const existing = entries.get(identity.key);
      if (existing) existing.points.push(point);
      else entries.set(identity.key, { ...identity, points: [point] });
    }
  }
  return [...entries.values()].sort((a, b) => a.name.localeCompare(b.name) || a.key.localeCompare(b.key));
}

export function exerciseProgressInRange(points: readonly ExerciseProgressPoint[], range: ExerciseProgressRange, now = Date.now()): ExerciseProgressPoint[] {
  const cutoff = range === 'all' ? -Infinity : now - range * 86_400_000;
  return points.filter(point => point.at >= cutoff && point.at <= now);
}

/** Records are descriptive within the selected range; ties retain the earliest achievement. */
export function exerciseProgressRecords(points: readonly ExerciseProgressPoint[]): ExerciseProgressRecords {
  const records: ExerciseProgressRecords = { maxLoadKg: null, maxReps: null, maxSeconds: null, volumeKg: null };
  for (const point of [...points].sort((a, b) => a.at - b.at || a.sessionId.localeCompare(b.sessionId))) {
    for (const key of ['maxLoadKg', 'maxReps', 'maxSeconds', 'volumeKg'] as const) {
      const value = point[key];
      if (value !== null && (!records[key] || value > records[key]!.value)) {
        records[key] = { value, completedAt: point.completedAt, sessionId: point.sessionId,
          ...(key === 'maxReps' && point.repsLoadKg !== null ? { loadKg: point.repsLoadKg } : {}) };
      }
    }
  }
  return records;
}
